<template>
  <v-container
    fluid
    class="download-report-page pt-0"
    :class="drawer ? 'drawer-open' : 'drawer-closed'"
  >
    <v-row align="start" justify="start" class="mt-1">
      <v-col cols="12" :class="{ 'pt-0': IS_MOBILE }">
        <v-data-table
          :class="!IS_MOBILE ? 'mt-3' : 'd-none-empty-sort'"
          :headers="headers"
          :items="exports"
          :loading="exportsLoading"
          :server-items-length="totalExports"
          :options.sync="options"
          :footer-props="footerProps"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>
                {{ $localizationService.localize("download_report_page.title") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                @click="openRequestDialog"
                :loading="requestingExport"
              >
                {{ $localizationService.localize("download_report_page.request_export_btn") }}
              </v-btn>
            </v-toolbar>
          </template>
            <template v-slot:[`item.exportType`]="{ item }">
              {{ getExportTypeLabel(item.exportType) }}
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                dark
                small
              >
                {{ getStatusLabel(item.status) }}
              </v-chip>
            </template>

            <template v-slot:[`item.createdAt`]="{ item }">
              {{ momentDateAndTime(item.createdAt) }}
            </template>

            <template v-slot:[`item.completedAt`]="{ item }">
              {{ item.completedAt ? momentDateAndTime(item.completedAt) : '-' }}
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip bottom open-delay="500" v-if="item.status === 'COMPLETED'">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    medium
                    @click="downloadExport(item)"
                    class="mr-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-download
                  </v-icon>
                </template>
                <span>{{ $localizationService.localize("download_report_page.download_tooltip") }}</span>
              </v-tooltip>

              <v-tooltip bottom open-delay="500" v-if="item.status === 'PENDING' || item.status === 'IN_PROGRESS'">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    medium
                    @click="refreshStatus(item)"
                    class="mr-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-refresh
                  </v-icon>
                </template>
                <span>{{ $localizationService.localize("download_report_page.refresh_tooltip") }}</span>
              </v-tooltip>

              <v-tooltip bottom open-delay="500">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    medium
                    color="red"
                    @click="deleteExport(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-delete
                  </v-icon>
                </template>
                <span>{{ $localizationService.localize("download_report_page.delete_tooltip") }}</span>
              </v-tooltip>
            </template>

            <template v-slot:[`item.errorMessage`]="{ item }">
              <v-tooltip bottom v-if="item.errorMessage">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="error" v-bind="attrs" v-on="on">
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>{{ item.errorMessage }}</span>
              </v-tooltip>
              <span v-else>-</span>
            </template>
          </v-data-table>
      </v-col>
    </v-row>

    <!-- Request Export Dialog -->
    <v-dialog v-model="requestDialog" max-width="500px">
      <v-card :loading="requestingExport">
        <v-card-title>
          <span class="headline">{{ $localizationService.localize("download_report_page.request_dialog.title") }}</span>
        </v-card-title>

        <v-card-text>
          <v-container v-if="requestDialog">
            <v-row>
              <v-select
                v-model="selectedExportType"
                :items="exportTypes"
                item-text="label"
                item-value="value"
                :label="$localizationService.localize('download_report_page.request_dialog.export_type_label')"
                :disabled="requestingExport"
                :rules="exportTypeRules"
                :menu-props="{ offsetY: true }"
              ></v-select>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="requestDialog = false"
            :disabled="requestingExport"
          >
            {{ $localizationService.localize("btn.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="requestExport"
            :disabled="!saveButtonActive"
          >
            {{ $localizationService.localize("btn.request") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from "vue";
import { mapState, mapActions } from "vuex";
import { IS_MOBILE, momentDateAndTime } from "../utils/utils";

const locService = Vue.prototype.$localizationService;

export default {
  name: "DownloadReportPage",

  routing: {
    localizationKey: "menu.tab.download_a_report",
    icon: "mdi-download",
    path: "/download",
    hasAccess: (user) => user.isAdmin || user.isManager,
  },

  data: () => ({
    IS_MOBILE,
    requestDialog: false,
    selectedExportType: null,
    downloadingIds: [],
    refreshingIds: [],
    deletingIds: [],
    options: {},
    footerProps: {
      "items-per-page-options": [25, 50, 100],
    },
    exportTypeRules: [
      (value) => !!value || "Required",
    ],
    headers: [
      {
        text: locService.localize("download_report_page.table.export_type"),
        value: "exportType",
        sortable: false,
      },
      {
        text: locService.localize("download_report_page.table.status"),
        value: "status",
        sortable: false,
      },
      {
        text: locService.localize("download_report_page.table.created_at"),
        value: "createdAt",
        sortable: false,
      },
      {
        text: locService.localize("download_report_page.table.completed_at"),
        value: "completedAt",
        sortable: false,
      },
      {
        text: locService.localize("download_report_page.table.error"),
        value: "errorMessage",
        sortable: false,
      },
      {
        text: locService.localize("download_report_page.table.actions"),
        value: "actions",
        sortable: false,
        align: "center",
      },
    ],
    exportTypes: [
      {
        label: locService.localize("download_report_page.export_types.assignments_all"),
        value: "ASSIGNMENTS_ALL",
      },
      {
        label: locService.localize("download_report_page.export_types.assignments_active"),
        value: "ASSIGNMENTS_ACTIVE",
      },
      {
        label: locService.localize("download_report_page.export_types.assignments_returned"),
        value: "ASSIGNMENTS_RETURNED",
      },
      {
        label: locService.localize("download_report_page.export_types.inventory"),
        value: "INVENTORY",
      },
    ],
  }),

  computed: {
    ...mapState("exportModule", [
      "exports",
      "exportsLoading",
      "totalExports",
      "requestingExport",
    ]),
    ...mapState("coreModule", ["drawer"]),

    saveButtonActive() {
      if (this.requestingExport) return false;

      if (
        this.exportTypeRules.find(
          (rule) => rule(this.selectedExportType) !== true,
        )
      ) {
        return false;
      }

      return true;
    },
  },

  watch: {
    options: {
      handler() {
        this.loadExportsData();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions("exportModule", [
      "loadExports",
      "requestExport",
      "getExportStatus",
      "getDownloadUrl",
      "deleteExport",
    ]),

    momentDateAndTime,

    async loadExportsData() {
      const { page, itemsPerPage } = this.options;
      await this.$store.dispatch("exportModule/loadExports", {
        page: (page || 1) - 1,
        size: itemsPerPage || 20,
      });
    },

    openRequestDialog() {
      this.selectedExportType = null;
      this.requestDialog = true;
    },

    async requestExport() {
      try {
        await this.$store.dispatch("exportModule/requestExport", {
          exportType: this.selectedExportType,
        });
        this.requestDialog = false;
        await this.loadExportsData();
      } catch (e) {
        // Error is already handled in the store
      }
    },

    async downloadExport(item) {
      this.downloadingIds.push(item.id);
      try {
        const url = await this.$store.dispatch("exportModule/getDownloadUrl", item.id);
        window.open(url, "_blank");
      } catch (e) {
        // Error is already handled in the store
      } finally {
        this.downloadingIds = this.downloadingIds.filter((id) => id !== item.id);
      }
    },

    async refreshStatus(item) {
      this.refreshingIds.push(item.id);
      try {
        await this.$store.dispatch("exportModule/getExportStatus", item.id);
      } catch (e) {
        // Error is already handled in the store
      } finally {
        this.refreshingIds = this.refreshingIds.filter((id) => id !== item.id);
      }
    },

    async deleteExport(item) {
      this.deletingIds.push(item.id);
      try {
        await this.$store.dispatch("exportModule/deleteExport", item.id);
      } catch (e) {
        // Error is already handled in the store
      } finally {
        this.deletingIds = this.deletingIds.filter((id) => id !== item.id);
      }
    },

    getExportTypeLabel(type) {
      const exportType = this.exportTypes.find((et) => et.value === type);
      return exportType ? exportType.label : type;
    },

    getStatusLabel(status) {
      const statusMap = {
        PENDING: locService.localize("download_report_page.status.pending"),
        IN_PROGRESS: locService.localize("download_report_page.status.in_progress"),
        COMPLETED: locService.localize("download_report_page.status.completed"),
        FAILED: locService.localize("download_report_page.status.failed"),
      };
      return statusMap[status] || status;
    },

    getStatusColor(status) {
      const colorMap = {
        PENDING: "orange",
        IN_PROGRESS: "blue",
        COMPLETED: "green",
        FAILED: "red",
      };
      return colorMap[status] || "grey";
    },
  },

  mounted() {
    this.loadExportsData();
  },
};
</script>

<style lang="scss">
.download-report-page {
  .d-none-empty-sort {
    .v-data-table-header {
      display: none;
    }
  }
}
</style>
